import { useAuth0 } from "@auth0/auth0-react";
import { EyeIcon } from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Badge, Button, Dialog, Paragraph, Select, SelectItem, Table } from "@fronterahealth/frontera-ui-components";

import { LearnerType, useLearnersQuery, useUpdateProviderLearnersMutation } from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

interface ProviderClientRow {
  id: string;
  globalId: string;
  clientName: string;
  caregiverNames: string;
  caregiverEmails: string;
  archived: boolean;
}

export const ProviderClientsTab: React.FC = () => {
  const go = useNavigate();
  const { isAuthenticated } = useAuth0();
  const { roles } = useAdminData();
  const { providersQuery, providerDetailsQuery } = useProvidersData();
  const { data } = providerDetailsQuery;

  const learnersQuery = useLearnersQuery(
    {},
    {
      queryKey: ["learners", isAuthenticated, JSON.stringify(roles)],
      retry: false,
      enabled:
        isAuthenticated &&
        !!roles.length &&
        (roles.includes("admin") || roles.includes("bcba") || roles.includes("assessment-bcba")),
      refetchOnWindowFocus: false,
    },
  );

  const allLearners = (learnersQuery?.data?.learner || []).filter(Boolean) as LearnerType[];

  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [selectedClientToAdd, setSelectedClientToAdd] = useState<SelectItem | null>(null);

  const updateProviderLearners = useUpdateProviderLearnersMutation({
    onSuccess: (res) => {
      if (!res.updateProviderLearners?.status) {
        notifyError(res.updateProviderLearners?.message || "Something went wrong updating this provider's clients.");
        return;
      }
      notifySuccess(res.updateProviderLearners?.message || "");
      providerDetailsQuery.refetch();
      providersQuery.refetch();
    },
    onError: () => {
      notifyError("Something went wrong updating this provider's clients.");
    },
  });

  const providerId = data?.providerById?.id;

  const allProviderLearners =
    data?.providerById?.learnerproviderSet?.edges
      ?.map((edge) => edge?.node?.learner)
      .filter((learner): learner is LearnerType => !!learner) || [];

  // Use ALL learner IDs (including archived) for mutations to avoid silently unmapping archived clients
  const learnerIdSet = allProviderLearners.map((learner) => learner.id);

  const onRemoveClient = async (id: string) => {
    if (!providerId) return;
    const newLearnerIdSet = learnerIdSet.filter((learnerId) => learnerId !== id);
    await updateProviderLearners.mutateAsync({ learners: newLearnerIdSet, providerId });
  };

  const onAddClient = async () => {
    if (!selectedClientToAdd?.id || !providerId) return;
    const newLearnerIdSet = [...learnerIdSet, selectedClientToAdd.id];
    await updateProviderLearners.mutateAsync({ learners: newLearnerIdSet, providerId });
    setSelectedClientToAdd(null);
    setAddClientDialogOpen(false);
  };

  // filter out clients already mapped to this provider
  const allLearnersItems = allLearners
    .filter((learner) => !learnerIdSet.includes(learner.id))
    .map((learner) => {
      const firstName = learner.learnermetadata?.firstName || "";
      const lastName = learner.learnermetadata?.lastName || "";
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();

      return {
        id: learner.id,
        primary: fullName,
        secondary: "",
        className: "ph-no-capture",
      };
    })
    .sort((a, b) => (a.primary || "").localeCompare(b.primary || "")) as SelectItem[];

  return (
    <div className="flex w-full flex-col items-start">
      <Dialog
        open={addClientDialogOpen}
        setOpen={setAddClientDialogOpen}
        title={"Add Client Mapping to Provider"}
        primaryButton={{
          text: "Add",
          onClick: async () => {
            await onAddClient();
          },
          disabled: !selectedClientToAdd,
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => {
            setSelectedClientToAdd(null);
            setAddClientDialogOpen(false);
          },
        }}
        size="xl"
        bodyClassName="max-h-[90vh]!"
      >
        <div className="flex flex-col gap-6">
          <Paragraph>
            Select an existing client to map to this provider’s profile. If you don’t see the client you’re looking for,
            they may not have a profile yet. You’ll need to create a new client profile to proceed.
          </Paragraph>
          <Select
            title={"Current Clients"}
            items={allLearnersItems}
            placeholderText={"Select a client"}
            selected={selectedClientToAdd}
            setSelected={setSelectedClientToAdd}
            enableSearch
          />
        </div>
      </Dialog>
      <Button
        onClick={() => {
          setAddClientDialogOpen(true);
        }}
        text={"Add Client"}
        variant="primary"
        buttonStyle="filled"
        className="self-end"
      />
      <div className="flow-root mt-8 w-full bg-white">
        <Table<ProviderClientRow>
          data={allProviderLearners.map((learner) => {
            const base64DecodedLearnerId = atob(learner.id);
            const databaseId = base64DecodedLearnerId.split("LearnerType:")?.pop();
            const learnerCaregiversMetadata = learner?.relationshipSet?.edges?.map(
              (node) => node?.node?.caregiver?.caregivermetadata,
            );
            return {
              id: databaseId || "<missing-database-id>",
              globalId: learner.id,
              clientName: `${learner.learnermetadata?.firstName} ${learner.learnermetadata?.lastName}`,
              caregiverEmails:
                learnerCaregiversMetadata
                  ?.map((caregiver) => caregiver?.email)
                  .filter(Boolean)
                  .join(", ") || "",
              caregiverNames:
                learnerCaregiversMetadata
                  ?.map((caregiver) => `${caregiver?.firstName || ""} ${caregiver?.lastName || ""}`.trim())
                  .filter(Boolean)
                  .join(", ") || "",
              archived: learner.archived === true,
            };
          })}
          actions={[
            {
              label: "View Client",
              Icon: EyeIcon,
              callback: (client) => {
                go(`/clients/${client.globalId}`);
              },
            },
            {
              label: "Remove Client",
              Icon: TrashIcon,
              callback: async (client) => {
                onRemoveClient(client.globalId);
              },
            },
          ]}
          columns={[
            { dataIndex: "id", key: "id", title: "Client ID" },
            {
              dataIndex: "clientName",
              key: "clientName",
              title: "Client Name",
              render: (value: string | boolean) => <span className="ph-no-capture">{value || "-"}</span>,
            },
            {
              dataIndex: "caregiverNames",
              key: "caregiverNames",
              title: "Caregiver Name(s)",
              render: (value: string | boolean) => <span className="ph-no-capture">{value || "-"}</span>,
            },
            {
              dataIndex: "caregiverEmails",
              key: "caregiverEmails",
              title: "Caregiver Email(s)",
              render: (value: string | boolean) => <span className="ph-no-capture">{value || "-"}</span>,
            },
            {
              dataIndex: "archived",
              key: "status",
              title: "Status",
              render: (archived) =>
                archived ? (
                  <Badge appearance="neutral" text="Archived" />
                ) : (
                  <Badge appearance="success" text="Active" />
                ),
            },
          ]}
          showSearch
          showPagination
        />
      </div>
    </div>
  );
};
